/**
 * PastComparisons — list + re-open persisted Delta Compare runs (Spec 184 FR-009).
 */
import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  useDeltaCompareRuns,
  useDeltaCompareRun,
  DeltaCompareResult,
} from '../../hooks/useDeltaCompare';

interface PastComparisonsProps {
  lakehouseId: string;
  onOpen: (result: DeltaCompareResult) => void;
}

export const PastComparisons: React.FC<PastComparisonsProps> = ({ lakehouseId, onOpen }) => {
  const { data: runs, isLoading } = useDeltaCompareRuns(lakehouseId);
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const { data: opened } = useDeltaCompareRun(openId);

  useEffect(() => {
    if (opened) {
      onOpen(opened);
      setOpenId(undefined);
    }
  }, [opened, onOpen]);

  if (isLoading || !runs || runs.length === 0) return null;

  return (
    <div className='bg-[var(--color-bg-card)] shadow rounded-lg p-6'>
      <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Past comparisons</h3>
      <p className='text-xs text-[var(--color-text-secondary)] mb-4'>
        Re-open a previously run comparison for this lakehouse.
      </p>
      <ul className='divide-y divide-[var(--color-border)]'>
        {runs.map((run) => (
          <li key={run.id} className='flex items-center justify-between py-2'>
            <div className='text-sm text-[var(--color-text-primary)]'>
              {formatDistanceToNow(new Date(run.comparison_timestamp), { addSuffix: true })}
              <span className='text-[var(--color-text-secondary)]'>
                {' '}
                — {run.tables_compared} tables, {run.tables_with_changes} changed, Δ
                {run.total_row_diff.toLocaleString()} rows
              </span>
            </div>
            <button
              type='button'
              onClick={() => setOpenId(run.id)}
              className='text-sm font-medium text-[var(--color-accent)] hover:opacity-80'
            >
              Re-open
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PastComparisons;
